/**
 * @fileoverview `GET /uk-imports/registration-summary` and
 * `PUT /uk-imports/registration-details` — the UK CBAM registration summary
 * (backend docs/features/uk-cbam-registration-summary D6, D2/D3).
 *
 * The server decides every item's status and the days left (D12); this only
 * parses dates once, as `useUkThreshold` does.
 *
 * The key is nested under the threshold prefix, so `invalidateUkThreshold` —
 * already called by "I've registered" and by every shipment write — refreshes
 * this page too. Nothing here has to remember to.
 */

import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import http from 'helpers/requests';
import { useOrgProfileStore } from 'modules/App/stores/orgProfileStore';
import { useUpdateDeclarant } from 'modules/Declarant/hooks/useUpdateDeclarant';
import { UK_THRESHOLD_QUERY_KEY, selectRegistration } from './useUkThreshold';

dayjs.extend(utc);

export const UK_REGISTRATION_SUMMARY_ENDPOINT = '/uk-imports/registration-summary';
export const UK_REGISTRATION_DETAILS_ENDPOINT = '/uk-imports/registration-details';
export const UK_REGISTRATION_SUMMARY_QUERY_KEY = [UK_THRESHOLD_QUERY_KEY, 'registration-summary'];

/** The wire response → dates parsed, items keyed. */
export const selectRegistrationSummary = (raw) =>
  raw
    ? {
        asOf: raw.asOf ? dayjs.utc(raw.asOf) : null,
        registration: selectRegistration(raw.registration),
        daysRemaining: typeof raw.daysRemaining === 'number' ? raw.daysRemaining : null,
        items: raw.items ?? [],
        itemsByKey: Object.fromEntries((raw.items ?? []).map((item) => [item.key, item])),
      }
    : null;

export function useUkRegistrationSummary() {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: UK_REGISTRATION_SUMMARY_QUERY_KEY,
    queryFn: async () => (await http.get(UK_REGISTRATION_SUMMARY_ENDPOINT))?.data ?? null,
    select: selectRegistrationSummary,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  return { summary: data ?? null, isLoading, isError, refetch };
}

/**
 * `{ sicCode: '24100' | null }` — the business classification (D2). Saved from
 * this page and from the organisation profile (D16), so both reads refresh.
 */
export function useUpdateRegistrationDetails() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ sicCode }) =>
      (await http.put(UK_REGISTRATION_DETAILS_ENDPOINT, { sicCode }))?.data ?? null,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: UK_REGISTRATION_SUMMARY_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: ['declarant'] }),
      ]),
  });
}

/**
 * The profile's own write (`PATCH /declarant`) for the facts edited inline here
 * (D16) — name, contact, address, EORI — then this page's summary refresh.
 * `useUpdateDeclarant` already refreshes `['declarant']`.
 */
export function useSaveDeclarantFields() {
  const queryClient = useQueryClient();
  const update = useUpdateDeclarant();
  return {
    isPending: update.isPending,
    save: async (patch) => {
      await update.mutateAsync(patch);
      await queryClient.invalidateQueries({ queryKey: UK_REGISTRATION_SUMMARY_QUERY_KEY });
    },
  };
}

/**
 * The page deep-links into the organisation profile to fix a missing EORI or
 * contact (D14); the profile's own save refreshes only `['declarant']`. So the
 * page re-reads its summary when the profile closes — nothing in Declarant has
 * to know this page exists.
 */
export function useRefreshSummaryOnProfileClose() {
  const queryClient = useQueryClient();
  const open = useOrgProfileStore((state) => state.open);
  const wasOpen = useRef(open);
  useEffect(() => {
    if (wasOpen.current && !open) {
      queryClient.invalidateQueries({ queryKey: UK_REGISTRATION_SUMMARY_QUERY_KEY });
    }
    wasOpen.current = open;
  }, [open, queryClient]);
}
